import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { siteConfig } from "../../lib/siteConfig";

export default function AdminLogin() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.body.classList.add("admin-login-open");
    return () => document.body.classList.remove("admin-login-open");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/login", { email, password });
      login(data.token, data.user);
      nav("/admin/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="admin-login-page min-h-[100dvh] bg-[#0A0A0B] text-slate-200 grid place-items-center px-4 py-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-10">
          <img src={siteConfig.logoUrl} alt={siteConfig.name} className="w-9 h-9 rounded-full object-cover ring-1 ring-[#2F6FA5]/50" />
          <div>
            <div className="text-sm font-semibold tracking-tight">{siteConfig.name}</div>
            <div className="text-xs text-slate-500">Admin Console</div>
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-[#121214] p-8">
          <h1 className="text-2xl font-semibold mb-1">Sign in</h1>
          <p className="text-sm text-slate-500 mb-8">Use your operator credentials issued by the platform team.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs text-slate-400 mb-2">Work email</label>
              <input data-testid="admin-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                className="w-full px-3 py-2.5 rounded-md bg-[#0A0A0B] border border-white/10 focus:border-white/30 focus:outline-none text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-2">Password</label>
              <div className="relative">
                <input data-testid="admin-password" type={show ? "text" : "password"} required value={password} onChange={(e)=>setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-20 rounded-md bg-[#0A0A0B] border border-white/10 focus:border-white/30 focus:outline-none text-sm" />
                <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 flex items-center gap-1">
                  {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {show ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {err && <div data-testid="admin-error" className="text-sm text-red-400 bg-red-900/20 border border-red-800/40 rounded-md p-3">{err}</div>}

            <button data-testid="admin-submit" type="submit" disabled={loading}
              className="w-full py-2.5 bg-white text-black font-medium rounded-md hover:bg-slate-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              <ShieldCheck className="w-4 h-4" /> {loading ? "Authenticating..." : "Sign in"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-white/5 flex justify-between text-xs text-slate-500">
            <span>Need access? <a href={`mailto:${siteConfig.supportEmail}`} className="text-slate-300 hover:underline">{siteConfig.supportEmail}</a></span>
            <span>v2.0</span>
          </div>
        </div>

        <p className="mt-6 text-xs text-slate-600 text-center">© 2026 {siteConfig.name} · 18+ only</p>
      </div>
    </div>
  );
}
